<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte"
    import flatpickr from "flatpickr"
    import { German } from "flatpickr/dist/l10n/de.js"
    import Icon from "../Icon.svelte"
    import { mdiCalendarCheckOutline, mdiCalendarEdit } from "@mdi/js"

    export let dateValue: Date,
        placeholder: string,
        id: string,
        classList: string = "",
        editMode = true,
        disabled: boolean = false,
        timestamp: boolean = false,
        greyMode: boolean = false,
        hideTime: boolean = false

    const dispatch = createEventDispatcher()

    let datepickerElement: HTMLInputElement
    let hasValue = Boolean(dateValue)
    let focus = false

    onMount(() => {
        if (editMode) {
            const dialogElement = datepickerElement.closest("dialog")
            const fp = flatpickr(datepickerElement, {
                enableTime: !hideTime,
                dateFormat: hideTime ? "d.m.Y" : "Y.m.d H:i",
                locale: German,
                altInput: true,
                altFormat: hideTime ? "d.m.Y" : "d.m.Y | H:i",
                defaultDate: dateValue,
                appendTo: dialogElement || document.body,
                onOpen: function (selectedDates, dateStr, instance) {
                    instance._positionCalendar()
                    if (dialogElement) {
                        setTimeout(() => {
                            const calendar = instance.calendarContainer
                            const inputRect = datepickerElement.getBoundingClientRect()
                            const dialogRect = dialogElement.getBoundingClientRect()

                            const topOffset = dialogRect.top - inputRect.top + 50
                            const leftOffset = dialogRect.left - inputRect.left

                            calendar.style.position = "absolute"
                            calendar.style.top = `${topOffset}px`
                            calendar.style.left = `${leftOffset}px`
                        }, 100)
                    }
                },
                onChange: (selectedDates) => {
                    dateValue = selectedDates[0]
                    hasValue = Boolean(dateValue)
                    dispatch("change", { date: dateValue })
                },
            })

            datepickerElement.placeholder = ""
        }
    })

    function clearDate() {
        if (!editMode) return
        dateValue = null
        hasValue = false
        datepickerElement._flatpickr.clear()
        dispatch("change", { date: dateValue })
    }

    function toggleDatepicker(force = false) {
        if (!editMode) return
        if (timestamp && force === true) {
            if (dateValue) {
                dateValue = null
                hasValue = false
                datepickerElement._flatpickr.clear()
                dispatch("change", { date: dateValue })
            } else {
                dateValue = new Date()
                hasValue = true
                datepickerElement._flatpickr.setDate(dateValue)
                dispatch("change", { date: dateValue })
            }
            return
        }
        if (dateValue) clearDate()
        else datepickerElement._flatpickr.open()
    }

    function onIconClick(event) {
        event.stopPropagation()
        if (!editMode) return
        toggleDatepicker(true)
    }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<label
    id="{id}"
    class="custom-date-picker"
    class:greyMode="{greyMode}"
    on:click|preventDefault|stopPropagation="{() => toggleDatepicker()}"
>
    <p class:hasValue="{hasValue || focus}">{placeholder}</p>
    <div class="wrapper">
        {#if editMode}
            <input
                type="text"
                bind:this="{datepickerElement}"
                class="{classList}"
                placeholder=""
                name="{id}"
                id="{id}"
                disabled="{disabled}"
                on:focus="{() => (focus = true)}"
                on:blur="{() => (focus = false)}"
            />
        {:else if !editMode}
            {#if dateValue}
                <div
                    class="no-input"
                    style="opacity: 0.5; font-size: 0.7rem; margin-top: 0.3rem;"
                >
                    {new Date(dateValue).toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                    })} | {new Date(dateValue).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                </div>
            {:else}
                <div class="no-input"></div>
            {/if}
        {/if}
        {#if editMode || timestamp}
            <button
                class="btn transparent icon"
                on:click|preventDefault|stopPropagation="{onIconClick}"
            >
                {#if !dateValue}
                    <Icon
                        path="{mdiCalendarEdit}"
                        width="24px"
                        height="24px"
                    />
                {:else}
                    <Icon
                        path="{mdiCalendarCheckOutline}"
                        width="24px"
                        height="24px"
                    />
                {/if}
            </button>
        {/if}
    </div>
</label>

<style
    lang="less"
    global
>
    .custom-date-picker {
        min-height: 54px;

        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        border-radius: 12px;
        border: 1px solid var(--grey-200);
        background: var(--white-100);
        padding: 0.7rem;
        position: relative;
        width: 100%;
        height: 100%;
        align-items: center;

        &.checkbox {
            max-width: 300px;
        }

        p {
            top: 50%;
            transform: translateY(-50%);
            position: absolute;
            padding-left: 0.7rem;
            width: 100%;
            text-align: left;
            text-transform: uppercase;
            transition: top 0.3s ease-out, transform 0.3s ease-out, opacity 0.3s ease-out, font-size 0.3s ease-out;

            &.hasValue {
                transform: translateY(0%);
                top: 0.4rem;
                display: block;
                opacity: 1;
                font-weight: 700;
            }
        }

        .wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0rem !important;
            padding-left: 0px !important;
            width: 100%;
            .btn {
                display: flex;
                align-items: center;
                color: var(--text-invers-100);

                &.icon {
                    opacity: 0.75;
                }
            }
            * {
                outline: none;
                border: none;
            }
            input[type="datetime-local"],
            .no-input {
                flex-grow: 1;
                padding-left: 0rem;
                width: 100%;
                text-align: start;
                display: flex;
                justify-content: flex-start;
                margin-bottom: 0rem;

                &:focus {
                    outline: none;
                }
                &:disabled {
                    opacity: 0.5;
                }
            }

            .no-input {
                color: var(--text-100);
                border-bottom: none !important;
                padding: 0px;
            }
        }
    }

    /* Custom styles for Flatpickr modal */
    .flatpickr-calendar {
        background-color: var(--white-100);
        border: 1px solid var(--grey-200);
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    .flatpickr-time {
        background-color: var(--white-100);
    }

    .flatpickr-month {
        background-color: var(--white-100);
        color: var(--text-invers-100);
    }

    .flatpickr-day {
        color: var(--text-invers-100);
    }

    .flatpickr-day:hover {
        background-color: var(--grey-100);
    }

    .flatpickr-day.selected {
        background-color: var(--primary-100);
        color: var(--white-100);
    }

    .flatpickr-day.today {
        border-color: var(--primary-100);
    }

    .flatpickr-time input {
        background: none;
        border: none;
        color: var(--text-invers-100);
    }

    .flatpickr-time .numInputWrapper span.arrowUp,
    .flatpickr-time .numInputWrapper span.arrowDown {
        color: var(--text-invers-100);
    }
    .greyMode {
        border: 1px solid var(--grey-200) !important;
        background: var(--text-100) !important;
        color: white;
        * {
            color: white !important;
            background: var(--text-100);
        }
    }
</style>
